import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GraduationCap } from "@phosphor-icons/react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    const res = await register(name, email, password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-between p-8 lg:p-16">
        <Link to="/" className="flex items-center gap-3 w-fit">
          <div className="w-9 h-9 bg-[#E25822] flex items-center justify-center">
            <GraduationCap size={22} weight="bold" color="#FFFFFF" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">UzviS Learning</span>
        </Link>

        <div className="max-w-md w-full mx-auto lg:mx-0 page-fade">
          <div className="eyebrow mb-3">Uzvi Services • Create account</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-none mb-3">
            Join your team.
          </h1>
          <p className="text-[#5C5C58] mb-10">All new accounts start with employee access. Please use your <span className="font-semibold text-[#1C1C19]">UzviS work email</span>.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">Full name</Label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                placeholder="Jane Doe"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">Work email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                placeholder="jane@company.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-[#993333] border-l-2 border-[#993333] pl-3 py-1 bg-red-50" data-testid="register-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg text-base font-medium"
              data-testid="register-submit-button"
            >
              {submitting ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-[#5C5C58]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#E25822] font-semibold hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </div>

        <div className="text-xs text-[#A8A8A1] mt-12">© {new Date().getFullYear()} Uzvi Services. Internal use only.</div>
      </div>

      <div className="hidden lg:block relative bg-[#1C1C19] overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/4ad7c645-3bf7-479a-a30f-948c8de2968e/images/550f5db9f718610968e8c4413a87b44d456b29e1380cab44b8371b53c27db440.png"
          alt="Architectural sunset corporate building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1C1C19]/80 via-[#1C1C19]/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/80 mb-5">
            Uzvi Services • Onboarding
          </div>
          <h2 className="font-heading text-4xl xl:text-5xl font-semibold tracking-tight leading-tight max-w-md">
            Track progress. Master skills. Grow at UzviS.
          </h2>
        </div>
      </div>
    </div>
  );
}
